<template>
  <div class="game-offer-root">
    <component
      :is="offerComponent"
      v-if="product"
      ref="offerTemplate"
      :is-split-view="isSplitView"
      @componentReady="updateComponent" />

    <footer-seo-text
      :text="seoText"
      class="container" />

    <div data-tf-live="01JD7NB5BDS8ABRERF68WDXCHR" />
  </div>
</template>

<script>
import { extractIdFromSlug } from 'Root/helpers/product';
import {
  getRandomBetween, scrollTo, setNotFoundState, slugify,
} from 'Root/helpers/tools';
import { SECONDS_PER_THREE_MONTHS } from 'Root/helpers/DateConstants';
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import { getProductTitleAndDescription } from 'Root/helpers/seo';
import AppModel from 'Models/AppModel';
import GameModel from 'Models/GameModel';
import UserModel from 'Models/UserModel';
import seoMixin from 'Root/mixins/seoMixin';
import FooterSeoText from 'Components/FooterSeoText.vue';
import {
  ABTestPaypalPaymentSystem,
  ABTestCheckoutPaymentSystem,
  ABTestPriceDecreaseForNewUsers,
  ABTestUSFirstPurchaseCommission,
  ABTestEpochPaymentSystem,
} from 'Root/drivers/ABTestsDriver';
import RegionModel from 'Models/RegionModel';
import CurrencyOfferModel from 'Models/CurrencyOfferModel';
import GameOfferAccounts from 'Root/pages/game/gameOffer/GameOfferAccounts.vue';
import GameOfferDefault from 'Root/pages/game/gameOffer/GameOfferDefault.vue';
import GameOfferCurrency from 'Root/pages/game/gameOffer/GameOfferCurrency.vue';
import GameOfferGoldOld from 'Root/pages/game/gameOffer/GameOfferGoldOld.vue';
import GameOfferAccount from 'Root/pages/game/gameOffer/GameOfferAccount.vue';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import axios from 'axios';

function checkProductAvailability(product, currentGameId, currentRegionId) {
  if (!product) {
    return false;
  }

  if ('game' in product && +product.game !== currentGameId) {
    return false;
  }

  if ('regions' in product && !product.regions.find((region) => region.id === currentRegionId)) {
    return false;
  }

  return true;
}

function getBackgrounds(product) {
  const backgrounds = [];
  let cssClass = 'offer-bg-img';

  if (!product.picture_item) {
    cssClass += ' offer-bg-img--transparent';
  }

  if (product.picture_bg) {
    backgrounds.push({
      default: `/storage${product.picture_bg}`,
      fallback: '/images/games/picture_bg.jpg',
      cssClass,
      shouldShowGradient: true,
      isSmall: product.product_currency_setting_id, // gold offers
    });
  }

  return {
    backgrounds,
  };
}

function updateHeaderInfo(store, route) {
  const { product, accountsPresets } = store.state.products;

  const presetId = +route.query?.preset_id;
  const activePreset = accountsPresets?.find((preset) => preset.id === presetId);

  const headerInfoData = {
    title: activePreset?.seo_title || product.seo_heading || product.title,
  };

  if (product.product_currency_setting_id || product.meta?.servers) {
    const headerImagePath = product.picture_item_small || product.picture_item;

    headerInfoData.image = headerImagePath && `/storage${headerImagePath}`;
    headerInfoData.fallbackImage = '/images/games/picture_item.png';
    headerInfoData.description = (presetId
      ? activePreset?.seo_content_header
      : product.product_currency_setting?.description) || '';

    headerInfoData.advantages = {
      key: product.product_currency_setting_id ? 'LIST_CURRENCY' : 'LIST_GOLD', // CURRENCY - Маркетплейс, GOLD - Старая голда
      isIconCompact: false,
    };

    headerInfoData.rating = {
      rate: product.trustpilot_reviews_rate,
      count: product.trustpilot_reviews_count,
    };
  } else {
    headerInfoData.reviews = product.trustpilot_reviews_count;
  }

  store.commit('products/mutateEntity', {
    entity: 'headerInfo',
    data: headerInfoData,
  });
}

function userCanSeePage(store, route) {
  const userCanSeePreview = [UserModel.ROLES.ADMIN.id, UserModel.ROLES.CONTENT_MANAGER.id].includes(
    store.state.user?.role,
  );

  if (route.query.preview && !userCanSeePreview) {
    return false;
  }

  return true;
}

async function getUserData(context) {
  const userRequestParams = {};
  const query = context.url.split('?');
  if (query[1]) {
    const user = query[1].match(new RegExp('(?<=_user=)(?:(?!&).)*', 'g'));

    if (user && user[0]) {
      [userRequestParams._user] = user;
    }
  }

  const cookie = Object.entries(context.cookies).map(([key, value]) => `${key}=${value};`).join(' ');

  return axios.get(`${AppModel.INTERNAL_URL}/shop/user`, {
    headers: {
      cookie,
      'X-Forwarded-For': context.connectionInfo.userIp,
    },
    params: userRequestParams,
  });
}

export default {
  name: 'GameOffer',

  components: {
    GameOfferDefault,
    GameOfferCurrency,
    GameOfferGoldOld,
    GameOfferAccounts,
    GameOfferAccount,
    FooterSeoText,
  },

  mixins: [seoMixin],

  seo() {
    // prettier-ignore
    const urn = typeof window === 'undefined'
      ? this.$ssrContext.url
      : window.location.pathname;

    const { title: baseTitle, description: baseDescription } = getProductTitleAndDescription(this.product);

    // BASE SEO PARAMS
    const brand = GameModel.ID_TO_DATA[+this.product?.game]?.fullTitle;

    let title = baseTitle;
    let description = baseDescription;
    let price = this.product ? this.product[`cost_${this.activeRegion.shortTitle.toLowerCase()}`] : null;
    let inStock = this.product?.available_in_regions?.includes(this.activeRegion.id);
    let robots = this.product?.meta?.is_hidden_for_seo_indexing === true ? 'noindex' : null;
    let seoImage = null;

    if (this.product) {
      const productImage = this.product.picture_og
          || this.product.picture_item_small
          || this.product.picture_item
          || this.product.picture_bg;
      seoImage = productImage && `/storage${productImage}`;
    }

    const activePreset = this.accountsPresets?.find((preset) => preset.id === +this.$route.query?.preset_id);

    let canonical = this.product?.canonical_url;

    if (activePreset?.id) {
      canonical = `${AppModel.URL}${this.$route.fullPath}`;
    }

    const productSchema = {
      name: title,
      description,
      brand,
      image: seoImage,
      url: this.$route.fullPath,
      offer: {
        type: 'Offer',
        url: this.$route.fullPath,
        priceCurrency: this.activeRegion.currency.title,
        price,
        inStock,
      },
      rating: {
        value: this.product?.trustpilot_reviews_rate || null,
        count: this.product?.trustpilot_reviews_count || null,
      },
    };

    // ADDITIONAL SEO PARAMS FOR ACCOUNTS & CURRENCY PAGES
    if (this.offerComponent === 'GameOfferAccounts' || this.offerComponent === 'GameOfferCurrency') {
      const micromarkup = this.product?.micromarkup ?? {};

      const {
        lowPrice,
        highPrice,
        ratingValue,
        ratingCount,
        total,
      } = micromarkup;

      inStock = !!total;

      const normalizeLowPrice = lowPrice?.usa
        ? this.calcPriceFromUsd(this.activeRegion.id, +lowPrice.usa, false, 2, true)
        : null;

      const normalizeHighPrice = highPrice?.usa
        ? this.calcPriceFromUsd(this.activeRegion.id, +highPrice.usa, false, 2, true)
        : null;

      productSchema.offer = {
        type: 'AggregateOffer',
        url: this.$route.fullPath,
        priceCurrency: this.activeRegion.currency.title,
        lowPrice: normalizeLowPrice,
        highPrice: normalizeHighPrice,
        offerCount: total,
        inStock,
      };

      productSchema.rating = {
        value: ratingValue || null,
        count: ratingCount || null,
      };

    // ADDITIONAL SEO PARAMS FOR ACCOUNT OFFER PAGE
    } else if (this.offerComponent === 'GameOfferAccount') {
      title = this.accountOffer?.outer_name || baseTitle;
      description = this.accountOffer?.description || baseDescription;
      price = this.calcPriceFromUsd(this.activeRegion.id, +this.accountOffer?.price_per_step, false, 2, true);
      inStock = this.accountOffer?.status === CurrencyOfferModel.STATUSES.ACTIVE;
      seoImage = this.accountOffer?.image ? this.accountOffer.image : seoImage;

      productSchema.name = title;
      productSchema.description = description;
      productSchema.image = seoImage;
      productSchema.offer.price = price;
      productSchema.offer.inStock = inStock;

      robots = 'noindex, nofollow';
    }

    return {
      title: activePreset?.seo_meta_title || title,
      meta: {
        description: activePreset?.seo_meta_description || description,
        robots,
        'og:type': 'website',
        'og:title': title,
        'og:url': `${AppModel.URL}${urn}`,
        'og:image': seoImage,
        'og:description': activePreset?.seo_meta_description || description,
        'og:price:amount': price,
        'og:price:currency': this.activeRegion.currency.title,
        'product:brand': brand,
        'product:condition': 'new',
        'product:availability': inStock ? 'in stock' : 'out of stock',
        'product:price:amount': price,
        'product:price:currency': this.activeRegion.currency.title,
      },
      canonical,
      product: productSchema,
    };
  },

  beforeRouteUpdate(to, from, next) {
    if (to.name !== from.name) {
      scrollTo(0, 0);
      IntercomApiDriver.trackEvent('product-view', {
        item_id: from.params.id,
      });
    }
    next();
  },

  async asyncData({ store, router, context }) {
    const fullSlug = router.currentRoute.params.id;
    const productId = router.currentRoute.meta?.tagAsOfferInfo?.offerId || extractIdFromSlug(fullSlug);
    const offerId = router.currentRoute.params?.offerId;
    const isOfferPreview = router.currentRoute.query?.offer_preview;

    if (!productId || !Number.isInteger(+productId)) {
      console.error(`can't get productId from slug ${fullSlug}`);
      setNotFoundState(store, context);
      return Promise.resolve();
    }

    if (context.getCatalogJSONData() && Object.keys(context.getCatalogJSONData()).length !== 0) {
      store.dispatch('setCatalogJSONData', context.getCatalogJSONData());
    }

    if (
      router.currentRoute.params.namespace === 'wow'
            && router.currentRoute.query?.utm_medium === 'cpc'
            && productId !== 1023 // gold product
            && !context.cookies[`ab_test_involved_${ABTestPriceDecreaseForNewUsers.userGroup.testId}`]
    ) {
      const user = (await getUserData(context))?.data?.user;

      if ((!user || !(user?.customer_counters?.paid_orders_count > 0))
              && await ABTestPriceDecreaseForNewUsers.addUserToTest(context)) {
        await store.dispatch('setUserAddedToPriceDecrease', true);
      }
    }

    const cookie = Object.entries(context.cookies).map(([key, value]) => `${key}=${value};`).join(' ');
    return store.dispatch('products/getProductById', { id: productId, isOfferPreview, cookie })
      .then(({ data: product }) => {
        if (
          !checkProductAvailability(product, store.state.activeGame.id, store.state.activeRegion.id)
                      && !router.currentRoute.query.preview
        ) {
          setNotFoundState(store, context);
          return Promise.reject(new Error('Product not available'));
        }

        store.commit('products/mutateProduct', product);
        store.commit('mutateBackgrounds', getBackgrounds(product));

        return store.dispatch('products/fetchProductRaids').then(() => {
          if (
            (product.product_currency_setting?.is_active || isOfferPreview)
                        && offerId
          ) {
            if (!product.product_currency_setting?.id) {
              return null;
            }

            return store.dispatch('products/getAccountOfferAction', {
              currencySettingId: product.product_currency_setting.id,
              offerId,
              isPreview: isOfferPreview,
            });
          }

          return null;
        });
      })
      .then((accountOfferData) => {
        if (offerId && (!accountOfferData || accountOfferData.length === 0)) {
          setNotFoundState(store, context);
        }
        return null;
      })
      .catch((error) => {
        console.error('Error in asyncData:', error);
        setNotFoundState(store, context);
        return Promise.reject(error);
      });
  },

  afterAsyncData({ store, router }) {
    const { product } = store.state.products;
    const route = router.currentRoute;

    if (!product) {
      return;
    }

    if (!userCanSeePage(store, router.currentRoute)) {
      store.dispatch('setDefaultBackground');
      setNotFoundState(store);
      return;
    }

    store.commit('products/mutateActiveTag', product.master_tag_id);
    updateHeaderInfo(store, route);
  },

  data() {
    return {
      isSplitView: false,
      isPriceDecreaseUserAdded: false,
    };
  },

  computed: {
    ...mapState(['activeRegion', 'activeGame', 'userDevice', 'user']),
    ...mapState(
      'products',
      ['tags', 'activeTag', 'product', 'accountOffers', 'accountOffer', 'accountsPresets', 'productRaids'],
    ),
    ...mapGetters(['calcPriceFromUsd', 'isUsersFirstOrder']),

    isWow() {
      return this.activeGame.id === GameModel.WOW.id;
    },

    isGoldProduct() {
      return this.product.is_currency_offer_finance_report;
    },

    backRoute() {
      if (this.activeTag) {
        return this.activeTag._href;
      }

      return `/${this.activeGame.routeNamespace}-boost`;
    },

    seoText() {
      const presetId = +this.$route.query?.preset_id;

      if (!presetId) {
        return this.product?.seo_meta_content;
      }

      return this.accountsPresets?.find((preset) => preset.id === presetId)?.seo_content_footer;
    },

    offerComponent() {
      if (
        (
          this.product?.product_currency_setting?.is_active
          || this.$route.query.offer_preview
        )
        && this.$route.params?.offerId
      ) {
        return 'GameOfferAccount';
      }

      if (
        this.product?.product_currency_setting?.is_active
        && this.product.product_currency_setting?.marketplace_type === CurrencyOfferModel.TYPES.ACCOUNT
      ) {
        return 'GameOfferAccounts';
      }

      if (this.product.product_currency_setting?.is_active) {
        const component = this.checkMarketplaceSplitsUsers();

        return component || 'GameOfferCurrency';
      }

      /* TODO: Warning! Удалить компонент после тестов валюты на проде SKY-3296 */
      if (this.product.meta?.servers) {
        return 'GameOfferGoldOld';
      }

      return 'GameOfferDefault';
    },

    productId() {
      return this.$route.meta?.tagAsOfferInfo?.offerId || extractIdFromSlug(this.$route.params.id);
    },

    isAccountsPage() {
      return this.offerComponent === 'GameOfferAccounts'
        || this.offerComponent === 'GameOfferAccount';
    },
  },

  watch: {
    productId: {
      handler(newId) {
        if (newId) {
          this.getProductData(newId);
        }
      },
    },

    activeRegion(newRegion) {
      if (newRegion.id !== RegionModel.EU.id) {
        ABTestUSFirstPurchaseCommission.addUserToTest();
      }

      if (!checkProductAvailability(this.product, this.activeGame.id, this.activeRegion.id)) {
        setNotFoundState(this.$store);
      }
    },

    '$route.query': {
      handler() {
        updateHeaderInfo(this.$store, this.$route);
      },
    },

    // isAccountsPage() {
    //   if (this.isAccountsPage) {
    //     this.mountTypeform();
    //   } else {
    //     this.unmountTypeform();
    //   }
    // },

    isPriceDecreaseUserAdded(val) {
      if (val) {
        const {
          testId,
          groupId,
        } = ABTestPriceDecreaseForNewUsers.userGroup;

        googleTagAnalytics.fireGtagEvent(`ab_test_${testId}_${groupId}`);
      }
    },
  },

  async created() {
    // TODO SKY-2998 починить forward\back браузерную навигацию
    if (typeof window !== 'undefined') {
      let shouldFetchProduct = false;
      let shouldUpdateHeaderInfo = false;

      // пытаемся достать товар из vuex
      const product = this.product || this.findProductInStore(this.productId);

      if (product && this.checkCorrectProductForMarketplace(product)) {
        if (
          !checkProductAvailability(product, this.activeGame.id, this.activeRegion.id)
                && !userCanSeePage(this.$store, this.$route)
        ) {
          setNotFoundState(this.$store);
          return;
        }

        if (!this.product) {
          this.mutateProduct({
            ...product,
            _shouldActualize: true,
          });
        }

        if (this.product._shouldActualize) {
          updateHeaderInfo(this.$store, this.$route);
          this.mutateActiveTag(this.product.master_tag_id);
          this.mutateBackgrounds(getBackgrounds(this.product));
          shouldFetchProduct = true;
          shouldUpdateHeaderInfo = true;
        }
      } else {
        shouldFetchProduct = true;
        shouldUpdateHeaderInfo = true;
      }

      if (shouldFetchProduct) {
        this.mutateProduct({});

        this.getProductData(this.productId)
          .then(() => {
            if (shouldUpdateHeaderInfo) {
              updateHeaderInfo(this.$store, this.$route);
              this.mutateActiveTag(this.product.master_tag_id);
            }
          })
          .catch(() => {
            setNotFoundState(this.$store);
          });
      } else {
        this.actualizeSlug(this.product);

        await this.$store.dispatch('products/fetchProductRaids');
      }

      await ABTestEpochPaymentSystem.addUserToTest();
    }

    if (!this.$isSSR && this.$store.state.catalogJSONData === null) {
      const { data } = await this.$axios
        .get(`/storage/${process.env.FILESYSTEM_RESOURCE_CATALOG_DATA_PATH}?v=${Date.now()}`);

      await this.$store.dispatch('setCatalogJSONData', data);
    }

    if (
      this.isWow
            && !this.isGoldProduct
            && this.$route.query?.utm_medium === 'cpc'
            && (!this.user || this.isUsersFirstOrder)
    ) {
      if (await ABTestPriceDecreaseForNewUsers.addUserToTest()) {
        this.isPriceDecreaseUserAdded = true;
      }
    }

    await ABTestCheckoutPaymentSystem.addUserToTest();

    if (this.activeRegion.id !== RegionModel.EU.id) {
      await ABTestUSFirstPurchaseCommission.addUserToTest();
    }

    await ABTestPaypalPaymentSystem.addUserToTest();
  },

  mounted() {
    // this.showProductsDisabledPopup();
    IntercomApiDriver.trackEvent('product-view', {
      item_id: this.productId,
    });
    // this.mountTypeform();
  },

  updated() {
    // this.mountTypeform();
  },

  beforeDestroy() {
    this.mutateProduct(null);
    // this.unmountTypeform();
  },

  methods: {
    ...mapMutations(['mutateBackgrounds']),
    ...mapActions('products', ['getProductById']),
    ...mapMutations('products', ['mutateProduct', 'mutateEntity', 'mutateActiveTag']),

    getProductData(productId) {
      return this.getProductById({ id: productId })
        .then(({ data }) => {
          if (!data.id) {
            this.$router.push(this.backRoute);
          }

          if (
            !checkProductAvailability(data, this.activeGame.id, this.activeRegion.id)
                        || !userCanSeePage(this.$store, this.$route)
          ) {
            setNotFoundState(this.$store);
            return;
          }

          this.mutateProduct(data);
          this.actualizeSlug(data);
          this.mutateBackgrounds(getBackgrounds(data));
          this.mutateActiveTag(data.master_tag_id);
          updateHeaderInfo(this.$store, this.$route);

          this.$store.dispatch('products/fetchProductRaids');
        })
        .catch(console.error);
    },

    actualizeSlug(product) {
      /* eslint-disable camelcase */
      const fullSlug = this.$route.params.id;
      const { title_slug, id, title } = product;
      const realFullSlug = title_slug ? `${title_slug}-${id}` : `${slugify(title)}-${id}`;
      /* eslint-enable camelcase */

      if (!fullSlug || !realFullSlug) {
        return;
      }

      if (fullSlug !== realFullSlug) {
        const url = this.$route.fullPath.replace(fullSlug, realFullSlug);
        this.$router.replace(url);
      }
    },

    findProductInStore(productId) {
      let product;
      const storeProducts = this.$store.state.products;

      // ищем в каталоге
      if (storeProducts.products.length) {
        product = storeProducts.products.find((storeProduct) => storeProduct.id === +productId);
      }

      // ищем в hot\popular товарах (главная игры)
      if (!product && storeProducts.todayTop?.offers) {
        const todayTop = Object.values(storeProducts.todayTop.offers);
        for (let i = 0; i < todayTop.length; i++) {
          product = todayTop[i].find((todayTopProduct) => todayTopProduct.id === +productId);

          if (product) {
            break;
          }
        }
      }

      return product;
    },

    checkMarketplaceSplitsUsers() {
      if (this.product.id !== Number(process.env.CURRENCY_MARKETPLACE_SPLIT_USERS_GAME_ID)) {
        return null;
      }

      this.isSplitView = true;

      const groups = ['GameOfferCurrency', 'GameOfferGoldOld'];
      const settingKey = 'marketplaceVision';
      // prettier-ignore
      const cookieSetting = typeof window === 'undefined'
        ? this.$ssrContext.cookies[settingKey]
        : this.$cookie.get(settingKey);

      const [userOldIndex, oldGroupsWeight] = cookieSetting
        ? decodeURIComponent(cookieSetting)
          .split(':')
          .map((item) => Number(item))
        : [];

      const groupWeight = Number(process.env.CURRENCY_MARKETPLACE_SPLIT_USERS_WEIGHT) || 20;
      const maxWeight = 100;
      // prettier-ignore
      const groupArray = [
        ...Array(groupWeight)
          .fill(groups[0]),
        ...Array(maxWeight - groupWeight)
          .fill(groups[1]),
      ];

      let marketplaceUserIndex = null;

      if (userOldIndex <= oldGroupsWeight || (userOldIndex > oldGroupsWeight && oldGroupsWeight === groupWeight)) {
        marketplaceUserIndex = userOldIndex;
      } else {
        marketplaceUserIndex = getRandomBetween(0, maxWeight - 1);

        if (typeof window !== 'undefined') {
          this.$cookie.set(settingKey, `${marketplaceUserIndex}:${groupWeight}`, {
            'max-age': SECONDS_PER_THREE_MONTHS,
          });
        } else {
          this.$ssrContext._setCookie[settingKey] = {
            value: `${marketplaceUserIndex}:${groupWeight}`,
            options: { maxAge: SECONDS_PER_THREE_MONTHS * 1000 },
          };
        }
      }

      return groupArray[marketplaceUserIndex];
    },

    checkCorrectProductForMarketplace(product) {
      if (!product?.product_currency_setting) {
        return true;
      }

      // eslint-disable-next-line no-prototype-builtins
      return product.product_currency_setting.hasOwnProperty('product_currency_setting_fields');
    },

    // mountTypeform() {
    //   if (!this.isAccountsPage || this.user) {
    //     return;
    //   }
    //
    //   // check if the script is already mounted
    //   if (document.getElementById('typeform-script')) {
    //     const sideTab = document.querySelector('.tf-v1-sidetab');
    //     if (sideTab) {
    //       sideTab.style.display = 'block';
    //     }
    //
    //     return;
    //   }
    //
    //   const script = document.createElement('script');
    //   script.id = 'typeform-script';
    //   script.src = 'https://embed.typeform.com/next/embed.js';
    //   script.async = true;
    //
    //   document.body.appendChild(script);
    // },

    // unmountTypeform() {
    //   // hide the side tab
    //   const sideTab = document.querySelector('.tf-v1-sidetab');
    //   if (sideTab) {
    //     sideTab.style.display = 'none';
    //   }
    // },

    updateComponent() {
      if (this.offerComponent === 'GameOfferAccounts') {
        updateHeaderInfo(this.$store, this.$route);
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
